/**
 * AdminZonas.jsx · v2.8.7.6 · Feb-2026
 * ─────────────────────────────────────────────────────────────────────────
 * Refactor: sidebar Sucursal → Zona + editor centralizado (4 tabs):
 *   1. Mapa & Polígono
 *   2. Datos básicos
 *   3. Reglas de negocio
 *   4. Técnicos asignados
 */
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  MapContainer, TileLayer, useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import {
  Loader2, ChevronRight, ChevronDown, MapPin, Save, Power, PowerOff,
  Plus, Search, Layers, Users, Trash2, Settings2, X, UserPlus, Warehouse,
  ExternalLink, Radio,
} from "lucide-react";
import ZonesConsolidatedMap from "../components/ZonesConsolidatedMap";

const CENTER_ECUADOR = [-1.831239, -78.183406];
const DEFAULT_ZOOM = 7;
const PLAN_TYPES = ["Residencial", "Corporativo", "PYME", "Enterprise"];

export default function AdminZonas() {
  const { hasPerm } = useAuth();
  const [tree, setTree] = useState({ items: [], total_zones: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [zone, setZone] = useState(null); // detalle completo de la zona seleccionada
  const [zoneLoading, setZoneLoading] = useState(false);
  const [expandedBranches, setExpandedBranches] = useState(new Set());
  const [q, setQ] = useState("");
  const [activeTab, setActiveTab] = useState("mapa");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [technicians, setTechnicians] = useState([]); // lista completa para autocomplete
  const [createModal, setCreateModal] = useState({ open: false, branchId: "", name: "" });

  // Cargar tree
  const loadTree = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/zonas/tree");
      setTree(data);
      // Auto-expandir todas las sucursales al inicio
      if (expandedBranches.size === 0 && data.items?.length) {
        setExpandedBranches(new Set(data.items.map((b) => b.branch_code || "_none")));
      }
    } catch (e) {
      toast.error("Error cargando zonas");
    } finally { setLoading(false); }
  };
  useEffect(() => { loadTree(); }, []);

  // Cargar detalle de zona
  const loadZone = async (id) => {
    if (!id) { setZone(null); return; }
    setZoneLoading(true); setDirty(false);
    try {
      const { data } = await api.get(`/admin/zonas/${id}`);
      setZone(data);
    } catch (e) {
      toast.error("Error cargando zona");
    } finally { setZoneLoading(false); }
  };
  useEffect(() => { loadZone(selectedId); }, [selectedId]);

  // Cargar técnicos (para autocomplete)
  useEffect(() => {
    api.get("/v1/users/technicians").then(({ data }) => setTechnicians(data.items || data || [])).catch(() => {});
  }, []);

  const toggleBranch = (code) => setExpandedBranches((prev) => {
    const nx = new Set(prev);
    nx.has(code) ? nx.delete(code) : nx.add(code);
    return nx;
  });

  // Filtro del árbol por búsqueda
  const filteredTree = useMemo(() => {
    if (!q.trim()) return tree.items;
    const qq = q.toLowerCase();
    return tree.items
      .map((b) => ({
        ...b,
        zones: (b.zones || []).filter((z) => (z.name || "").toLowerCase().includes(qq)),
      }))
      .filter((b) => b.zones.length > 0);
  }, [tree, q]);

  const patchZone = (patch) => {
    setZone((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  };

  const saveZone = async () => {
    if (!zone) return;
    setSaving(true);
    try {
      const payload = {
        name: zone.name,
        branch_id: zone.branch_id || null,
        polygon: zone.polygon,
        daily_capacity: zone.daily_capacity,
        active: zone.active,
        notes: zone.notes,
        max_ots_dia: zone.max_ots_dia,
        priority_residencial: zone.priority_residencial,
        blocked_plan_types: zone.blocked_plan_types || [],
        sla_multiplier: zone.sla_multiplier,
      };
      const { data } = await api.patch(`/admin/zonas/${zone.id}`, payload);
      setZone(data);
      setDirty(false);
      toast.success("Zona actualizada · A1/A2/A3 aplicarán al próximo ciclo");
      loadTree();
    } catch (e) {
      toast.error("Error al guardar: " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const addTechnicians = async (userIds) => {
    if (!zone || !userIds?.length) return;
    try {
      const { data } = await api.post(`/admin/zonas/${zone.id}/technicians`, { user_ids: userIds });
      toast.success(`Agregados ${data.added.length} · omitidos ${data.skipped.length}`);
      loadZone(zone.id); loadTree();
    } catch (e) { toast.error("Error al agregar técnicos"); }
  };

  const removeTech = async (userId) => {
    if (!zone) return;
    try {
      await api.delete(`/admin/zonas/${zone.id}/technicians/${userId}`);
      toast.success("Técnico removido");
      loadZone(zone.id); loadTree();
    } catch (e) { toast.error("Error al remover"); }
  };

  // v101.7.0 · Check: técnico en zona pero fuera del pool A2 (auto-agendamiento)
  const toggleTechAutoAssign = async (userId, enabled) => {
    if (!zone) return;
    try {
      await api.patch(`/admin/zonas/${zone.id}/technicians/${userId}/auto-assign`, {
        auto_assign_enabled: enabled,
      });
      setZone((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          technicians: (prev.technicians || []).map((t) =>
            t.user_id === userId ? { ...t, auto_assign_enabled: enabled } : t
          ),
        };
      });
      toast.success(enabled
        ? "Auto-agendamiento activado para este técnico"
        : "Auto-agendamiento desactivado · A2 no le asignará OTs");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar el check");
    }
  };

  // v2.8.9.1 · Crear zona (M19.zones.create) — con modal de sucursal obligatoria
  const openCreateModal = (prefillBranchId = "") => {
    if (!hasPerm("M19.zones.create")) return toast.error("Sin permiso M19.zones.create");
    setCreateModal({ open: true, branchId: prefillBranchId || "", name: "" });
  };
  const createZone = async () => {
    const { branchId, name } = createModal;
    if (!name.trim()) return toast.error("El nombre es obligatorio");
    if (!branchId) return toast.error("Selecciona una sucursal destino");
    setCreateModal((p) => ({ ...p, saving: true }));
    try {
      const { data } = await api.post(`/v1/config/zones`, {
        code: name.replace(/[^A-Za-z0-9_-]/g, "_").toUpperCase().slice(0, 32),
        name: name.trim(),
        branch_id: branchId,
      });
      toast.success(`Zona "${name}" creada — dibuja el polígono en el mapa`);
      setCreateModal({ open: false, branchId: "", name: "" });
      await loadTree();
      if (data?.id) setSelectedId(data.id);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(", ") : (detail || e.message);
      toast.error(`Error al crear zona: ${msg}`);
      setCreateModal((p) => ({ ...p, saving: false }));
    }
  };

  // v2.8.9.1 · Eliminar zona actual (M19.zones.delete)
  const deleteCurrentZone = async () => {
    if (!zone) return;
    if (!hasPerm("M19.zones.delete")) return toast.error("Sin permiso M19.zones.delete");
    if (!window.confirm(`¿Eliminar la zona "${zone.name}"? Esta acción es permanente.`)) return;
    try {
      const res = await api.delete(`/v1/config/zones/${zone.id}`);
      const unlinked = res.data?.unlinked;
      const detail = unlinked
        ? ` · desvinculado: ${Object.entries(unlinked).filter(([,n]) => n > 0).map(([k,n]) => `${n} ${k}`).join(", ") || "sin dependencias"}`
        : "";
      toast.success(`Zona "${zone.name}" eliminada${detail}`);
      setSelectedId(null);
      await loadTree();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar zona");
    }
  };

  return (
    <Layout>
      <div className="p-4" data-testid="admin-zonas-page">
        <div className="mb-3 flex items-center gap-3">
          <Layers size={22} style={{ color: "#0A3747" }} />
          <div className="flex-1">
            <h1 className="text-xl font-bold" style={{ color: "#0A3747" }}>Gestión de Zonas</h1>
            <div className="text-xs text-gray-500">
              Sucursal → Zona · Edición centralizada · Mapeo técnicos ↔ zona
              · {tree.total_zones} zonas
            </div>
          </div>
          <button
            onClick={loadTree}
            className="text-[11px] px-2 py-1 border rounded hover:bg-gray-50"
            data-testid="btn-reload-tree"
          >
            Actualizar
          </button>
          {hasPerm("M19.zones.create") && (
            <button
              onClick={() => openCreateModal(zone?.branch_id || "")}
              className="text-[11px] px-3 py-1 rounded bg-emerald-600 text-white font-bold hover:bg-emerald-700 flex items-center gap-1"
              data-testid="btn-create-zone"
              title="Crear una nueva zona operativa (M19.zones.create)"
            >
              <Plus size={12}/> Nueva Zona
            </button>
          )}
        </div>

        <div className="grid grid-cols-[320px_1fr] gap-3">
          {/* Sidebar Sucursal → Zona */}
          <aside className="bg-white border rounded-lg overflow-hidden" data-testid="zones-sidebar">
            <div className="p-2 border-b bg-gray-50 flex items-center gap-2">
              <Search size={14} className="text-gray-400"/>
              <input
                type="text" placeholder="Buscar zona…" value={q} onChange={(e) => setQ(e.target.value)}
                className="text-[12px] w-full outline-none" data-testid="zone-search"
              />
            </div>
            <div className="max-h-[75vh] overflow-y-auto py-1">
              {loading ? (
                <div className="p-6 text-center"><Loader2 size={16} className="animate-spin text-gray-400 inline-block"/></div>
              ) : filteredTree.length === 0 ? (
                <div className="p-4 text-center text-[11px] text-gray-400">Sin coincidencias</div>
              ) : filteredTree.map((b) => {
                const bkey = b.branch_code || "_none";
                const isOpen = expandedBranches.has(bkey);
                return (
                  <div key={bkey}>
                    <div className="flex items-center">
                      <button
                        onClick={() => toggleBranch(bkey)}
                        className="flex-1 flex items-center gap-1.5 px-2 py-1.5 text-left hover:bg-gray-50"
                        data-testid={`branch-toggle-${bkey}`}
                      >
                        {isOpen ? <ChevronDown size={12}/> : <ChevronRight size={12}/>}
                        <div className="flex-1 min-w-0">
                          <div className="text-[12px] font-bold" style={{ color: "#0A3747" }}>
                            {b.branch_code || "—"} · {b.branch_name}
                          </div>
                          <div className="text-[9px] text-gray-400">{b.zones_count} zonas</div>
                        </div>
                      </button>
                      {hasPerm("M19.zones.create") && b.branch_id && (
                        <button
                          onClick={(e) => { e.stopPropagation(); openCreateModal(b.branch_id); }}
                          className="mr-1 p-1 rounded hover:bg-emerald-100 text-emerald-600"
                          data-testid={`btn-create-in-${bkey}`}
                          title={`Crear zona en ${b.branch_name}`}
                        >
                          <Plus size={12}/>
                        </button>
                      )}
                    </div>
                    {isOpen && (
                      <div className="ml-3 border-l border-gray-200">
                        {b.zones.map((z) => (
                          <button
                            key={z.id}
                            onClick={() => setSelectedId(z.id)}
                            className={`w-full flex items-center gap-1.5 pl-2 pr-2 py-1 text-left hover:bg-blue-50 ${selectedId === z.id ? "bg-blue-100 border-l-2 border-l-blue-600" : ""}`}
                            data-testid={`zone-item-${z.id}`}
                          >
                            <MapPin size={10} className={z.active ? "text-emerald-600" : "text-gray-300"}/>
                            <div className="flex-1 min-w-0">
                              <div className="text-[11px] font-semibold truncate" style={{ color: "#0A3747" }}>{z.name}</div>
                              <div className="text-[9px] text-gray-500">
                                Cap: {z.daily_capacity || "—"} · <Users size={8} className="inline"/> {z.technicians_count}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </aside>

          {/* Panel principal */}
          <main className="bg-white border rounded-lg min-h-[75vh]">
            {!selectedId ? (
              <EmptyState tree={tree} onSelect={setSelectedId}/>
            ) : zoneLoading ? (
              <div className="p-12 text-center"><Loader2 size={22} className="animate-spin text-gray-400"/></div>
            ) : zone ? (
              <>
                <ZoneHeader
                  zone={zone} dirty={dirty} saving={saving} onSave={saveZone}
                  onToggleActive={() => patchZone({ active: !zone.active })}
                  onDelete={hasPerm("M19.zones.delete") ? deleteCurrentZone : null}
                />
                <div className="border-b flex" data-testid="zone-tabs">
                  {[
                    { id: "mapa", label: "Mapa & Polígono", icon: MapPin },
                    { id: "datos", label: "Datos básicos", icon: Settings2 },
                    { id: "reglas", label: "Reglas de negocio", icon: Layers },
                    { id: "tecnicos", label: `Técnicos (${zone.technicians?.length || 0})`, icon: Users },
                    { id: "bodegas", label: "Bodegas", icon: Warehouse },
                  ].map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setActiveTab(t.id)}
                      className={`px-4 py-2 text-[11px] font-semibold uppercase tracking-widest flex items-center gap-1.5 border-b-2 ${activeTab === t.id ? "border-blue-600 text-blue-700" : "border-transparent text-gray-500 hover:text-gray-700"}`}
                      data-testid={`tab-${t.id}`}
                    >
                      <t.icon size={12}/> {t.label}
                    </button>
                  ))}
                </div>
                <div className="p-4">
                  {activeTab === "mapa" && <MapaTab zone={zone} onChange={patchZone}/>}
                  {activeTab === "datos" && <DatosTab zone={zone} branches={tree.items} onChange={patchZone}/>}
                  {activeTab === "reglas" && <ReglasTab zone={zone} onChange={patchZone}/>}
                  {activeTab === "tecnicos" && (
                    <TecnicosTab
                      zone={zone} technicians={technicians}
                      onAdd={addTechnicians} onRemove={removeTech}
                      onToggleAutoAssign={toggleTechAutoAssign}
                    />
                  )}
                  {activeTab === "bodegas" && (
                    <BodegasTab zone={zone} branches={tree.items} />
                  )}
                </div>
              </>
            ) : null}
          </main>
        </div>

        {/* Modal crear zona */}
        {createModal.open && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setCreateModal({ open: false, branchId: "", name: "" })}>
            <div className="bg-white rounded-lg w-[420px] shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="create-zone-modal">
              <div className="p-4 border-b flex items-center gap-2">
                <Plus size={16} className="text-emerald-600"/>
                <div className="flex-1 font-bold text-[14px]" style={{ color: "#0A3747" }}>Nueva Zona</div>
                <button onClick={() => setCreateModal({ open: false, branchId: "", name: "" })} className="p-1 hover:bg-gray-100 rounded"><X size={14}/></button>
              </div>
              <div className="p-4 space-y-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold block mb-1">Sucursal destino *</label>
                  <select
                    value={createModal.branchId}
                    onChange={(e) => setCreateModal((p) => ({ ...p, branchId: e.target.value }))}
                    className="w-full px-2 py-1.5 border rounded text-[12px]"
                    data-testid="create-zone-branch-select"
                  >
                    <option value="">— Seleccionar sucursal —</option>
                    {tree.items.filter((b) => b.branch_id).map((b) => (
                      <option key={b.branch_id} value={b.branch_id}>{b.branch_code} · {b.branch_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold block mb-1">Nombre de la zona *</label>
                  <input
                    type="text" autoFocus
                    value={createModal.name}
                    onChange={(e) => setCreateModal((p) => ({ ...p, name: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === "Enter") createZone(); }}
                    placeholder="Ej: Zona Norte SD"
                    className="w-full px-2 py-1.5 border rounded text-[12px]"
                    data-testid="create-zone-name-input"
                  />
                </div>
              </div>
              <div className="p-3 border-t flex justify-end gap-2 bg-gray-50">
                <button
                  onClick={() => setCreateModal({ open: false, branchId: "", name: "" })}
                  className="text-[11px] px-3 py-1.5 border rounded"
                >
                  Cancelar
                </button>
                <button
                  onClick={createZone}
                  disabled={!createModal.branchId || !createModal.name.trim() || createModal.saving}
                  className={`text-[11px] px-4 py-1.5 rounded font-bold flex items-center gap-1 ${createModal.branchId && createModal.name.trim() && !createModal.saving ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-gray-200 text-gray-400 cursor-not-allowed"}`}
                  data-testid="btn-confirm-create-zone"
                >
                  {createModal.saving ? <Loader2 size={11} className="animate-spin"/> : <Plus size={11}/>}
                  Crear zona
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

/* ─── Sub-componentes ───────────────────────────────────────────────────── */

function EmptyState({ tree, onSelect }) {
  // v10 · Vista consolidada con ZonesConsolidatedMap + técnicos live
  const [techLive, setTechLive] = useState([]);
  const [techLoading, setTechLoading] = useState(false);

  const branches = useMemo(() => (tree?.items || []).map((b) => ({
    id: b.branch_id, code: b.branch_code, name: b.branch_name,
  })), [tree]);

  const allZones = useMemo(() => {
    const out = [];
    (tree?.items || []).forEach((b) =>
      (b.zones || []).forEach((z) => out.push({
        ...z,
        branch_id: b.branch_id,
        branch_code: b.branch_code,
        branch_name: b.branch_name,
        geometry_type: Array.isArray(z.polygon) && z.polygon.length >= 3 ? "polygon" : (z.geometry_type || "polygon"),
        geometry: z.geometry || { coords: z.polygon || [] },
      }))
    );
    return out;
  }, [tree]);

  const loadTechs = useCallback(async () => {
    setTechLoading(true);
    try {
      const { data } = await api.get("/v1/monitoring/live");
      setTechLive(data?.technicians || []);
    } catch { setTechLive([]); }
    finally { setTechLoading(false); }
  }, []);

  useEffect(() => { loadTechs(); const iv = setInterval(loadTechs, 30000); return () => clearInterval(iv); }, [loadTechs]);

  const activeTechs = techLive.filter((t) => t.last_ping?.lat);

  return (
    <div className="h-full flex flex-col" data-testid="empty-state">
      <div className="p-3 border-b bg-gradient-to-r from-slate-50 to-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Layers size={16} className="text-blue-600"/>
          <div>
            <div className="text-[13px] font-bold" style={{ color: "#0A3747" }}>Vista Global · {allZones.length} zonas</div>
            <div className="text-[10px] text-gray-500">
              Polígonos de cobertura + técnicos en tiempo real · Clic en una zona para editar
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {techLoading && <Loader2 size={12} className="animate-spin text-gray-400" />}
          <div className="flex items-center gap-1 text-[10px] text-gray-600">
            <Radio size={10} className="text-emerald-500" />
            <span><b>{activeTechs.length}</b> técnicos online</span>
          </div>
        </div>
      </div>
      <div style={{ height: 620 }} className="flex-1" data-testid="zones-global-map">
        <ZonesConsolidatedMap
          branches={branches}
          zones={allZones}
          technicians={techLive}
          height={620}
          onZoneClick={(z) => onSelect(z.id)}
          onBranchClick={() => {}}
        />
      </div>
    </div>
  );
}

// v3.1.0 · Polígono clicable en la vista global de zonas
function ClickablePolygon({ positions, color, label, onClick }) {
  const map = useMap();
  useEffect(() => {
    if (!Array.isArray(positions) || positions.length < 3) return;
    const poly = L.polygon(positions, {
      color, weight: 2, fillOpacity: 0.18,
    }).addTo(map);
    poly.bindTooltip(label, { permanent: false, direction: "center" });
    poly.on("click", onClick);
    return () => { try { map.removeLayer(poly); } catch (e) { /* noop */ } };
  }, [map, positions, color, label, onClick]);
  return null;
}

function ZoneHeader({ zone, dirty, saving, onSave, onToggleActive, onDelete }) {
  return (
    <div className="p-3 border-b flex items-center gap-2 bg-gradient-to-r from-slate-50 to-white">
      <MapPin size={18} className={zone.active ? "text-emerald-600" : "text-gray-400"}/>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="text-[15px] font-bold" style={{ color: "#0A3747" }}>{zone.name}</div>
          {zone.branch_code && (
            <span className="text-[9px] font-mono px-1.5 py-0.5 bg-slate-100 border rounded">
              {zone.branch_code} · {zone.branch_name}
            </span>
          )}
          {!zone.active && <span className="text-[9px] px-1.5 py-0.5 bg-gray-200 rounded text-gray-700">INACTIVA</span>}
        </div>
        <div className="text-[9px] text-gray-400 font-mono">{zone.id}</div>
      </div>
      <button
        onClick={onToggleActive}
        className={`text-[10px] px-2 py-1 rounded border ${zone.active ? "text-red-700 border-red-200 hover:bg-red-50" : "text-emerald-700 border-emerald-200 hover:bg-emerald-50"}`}
        data-testid="btn-toggle-active"
      >
        {zone.active ? <PowerOff size={11} className="inline mr-1"/> : <Power size={11} className="inline mr-1"/>}
        {zone.active ? "Desactivar" : "Activar"}
      </button>
      <button
        onClick={onSave} disabled={!dirty || saving}
        className={`text-[11px] px-3 py-1.5 rounded font-semibold flex items-center gap-1 ${dirty && !saving ? "bg-blue-600 text-white hover:bg-blue-700" : "bg-gray-100 text-gray-400 cursor-not-allowed"}`}
        data-testid="btn-save-zone"
      >
        {saving ? <Loader2 size={11} className="animate-spin"/> : <Save size={11}/>}
        {dirty ? "Guardar cambios" : "Sin cambios"}
      </button>
      {onDelete && (
        <button
          onClick={onDelete}
          className="text-[11px] px-2 py-1.5 rounded border border-red-300 text-red-700 hover:bg-red-50 flex items-center gap-1"
          data-testid="btn-delete-zone"
          title="Eliminar zona permanentemente (M19.zones.delete)"
        >
          <Trash2 size={11}/> Eliminar
        </button>
      )}
    </div>
  );
}

function MapaTab({ zone, onChange }) {
  const polygon = zone.polygon || [];
  const center = polygon.length ? polygon[0] : CENTER_ECUADOR;
  return (
    <div className="space-y-2">
      <div className="text-[10px] text-gray-500">
        Usa la barra de herramientas del mapa (arriba a la izquierda) para <b>dibujar</b>, <b>editar</b> o <b>eliminar</b> el polígono. Se guarda con &quot;Guardar cambios&quot;.
      </div>
      <div style={{ height: 520 }} className="rounded overflow-hidden border" data-testid="zone-map">
        <MapContainer center={center} zoom={polygon.length ? 12 : DEFAULT_ZOOM} style={{ height: "100%", width: "100%" }}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OSM"/>
          <DrawableZone polygon={polygon} onChange={(newP) => onChange({ polygon: newP })}/>
        </MapContainer>
      </div>
      <div className="text-[10px] text-gray-500">
        {polygon.length} vértices · {polygon.length > 0 ? `centroide ~${polygon[0][0].toFixed(4)},${polygon[0][1].toFixed(4)}` : "sin polígono definido"}
      </div>
    </div>
  );
}

// FeatureGroup con leaflet-draw controls (crear/editar/eliminar polígono)
function DrawableZone({ polygon, onChange }) {
  const map = useMap();
  const featureGroupRef = useRef(null);
  const drawControlRef = useRef(null);
  const onChangeRef = useRef(onChange);

  // Mantener onChange actualizada sin re-instanciar el control
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  // Inicializar FeatureGroup + Draw Control una sola vez por mapa
  useEffect(() => {
    if (!map) return;
    const fg = new L.FeatureGroup();
    fg.addTo(map);
    featureGroupRef.current = fg;

    const drawControl = new L.Control.Draw({
      position: "topleft",
      draw: {
        polygon: {
          allowIntersection: false,
          showArea: true,
          shapeOptions: { color: "#0A3747", weight: 2, fillOpacity: 0.15 },
          drawError: { color: "#e11d48", message: "<strong>Error:</strong> el polígono no puede cruzarse consigo mismo" },
        },
        polyline: false,
        rectangle: false,
        circle: false,
        marker: false,
        circlemarker: false,
      },
      edit: {
        featureGroup: fg,
        remove: true,
      },
    });
    map.addControl(drawControl);
    drawControlRef.current = drawControl;

    const extractLatLngs = (layer) => {
      const latlngs = layer.getLatLngs();
      const ring = Array.isArray(latlngs[0]) ? latlngs[0] : latlngs;
      return ring.map((ll) => [ll.lat, ll.lng]);
    };

    map.on(L.Draw.Event.CREATED, (e) => {
      // Solo permitir un polígono a la vez: limpiar los previos
      fg.clearLayers();
      fg.addLayer(e.layer);
      onChangeRef.current(extractLatLngs(e.layer));
    });
    map.on(L.Draw.Event.EDITED, (e) => {
      e.layers.eachLayer((layer) => {
        onChangeRef.current(extractLatLngs(layer));
      });
    });
    map.on(L.Draw.Event.DELETED, () => {
      onChangeRef.current([]);
    });

    return () => {
      map.off(L.Draw.Event.CREATED);
      map.off(L.Draw.Event.EDITED);
      map.off(L.Draw.Event.DELETED);
      if (drawControlRef.current) map.removeControl(drawControlRef.current);
      if (featureGroupRef.current) map.removeLayer(featureGroupRef.current);
      drawControlRef.current = null;
      featureGroupRef.current = null;
    };
  }, [map]);

  // Sincronizar polígono externo → FeatureGroup (al cambiar de zona seleccionada)
  useEffect(() => {
    const fg = featureGroupRef.current;
    if (!fg) return;
    fg.clearLayers();
    if (polygon && polygon.length >= 3) {
      const poly = L.polygon(polygon, { color: "#0A3747", weight: 2, fillOpacity: 0.15 });
      fg.addLayer(poly);
      try { map.fitBounds(poly.getBounds(), { padding: [20, 20] }); } catch (_) { /* noop */ }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(polygon)]);

  return null;
}

function DatosTab({ zone, branches, onChange }) {
  return (
    <div className="space-y-3 max-w-xl" data-testid="datos-tab">
      <Field label="Nombre">
        <input
          type="text" value={zone.name || ""} onChange={(e) => onChange({ name: e.target.value })}
          className="w-full px-2 py-1.5 border rounded text-[12px]" data-testid="input-name"
        />
      </Field>
      <Field label="Sucursal">
        <select
          value={zone.branch_id || ""} onChange={(e) => onChange({ branch_id: e.target.value || null })}
          className="w-full px-2 py-1.5 border rounded text-[12px]" data-testid="select-branch"
        >
          <option value="">— Sin asignar —</option>
          {branches.filter((b) => b.branch_id).map((b) => (
            <option key={b.branch_id} value={b.branch_id}>{b.branch_code} · {b.branch_name}</option>
          ))}
        </select>
      </Field>
      <Field label="Capacidad diaria (OTs máx)">
        <input
          type="number" min="0" value={zone.daily_capacity ?? ""}
          onChange={(e) => onChange({ daily_capacity: e.target.value === "" ? null : Number(e.target.value) })}
          className="w-32 px-2 py-1.5 border rounded text-[12px]" data-testid="input-daily-capacity"
        />
      </Field>
      <Field label="Notas">
        <textarea
          rows={3} value={zone.notes || ""} onChange={(e) => onChange({ notes: e.target.value })}
          className="w-full px-2 py-1.5 border rounded text-[12px]" data-testid="input-notes"
        />
      </Field>
    </div>
  );
}

function ReglasTab({ zone, onChange }) {
  const toggleBlockedPlan = (plan) => {
    const cur = zone.blocked_plan_types || [];
    const nx = cur.includes(plan) ? cur.filter((p) => p !== plan) : [...cur, plan];
    onChange({ blocked_plan_types: nx });
  };
  return (
    <div className="space-y-4 max-w-xl" data-testid="reglas-tab">
      <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
        Estas reglas se aplican por zona y son leídas por los agentes A1 (asignación) y A3 (bloqueos por evento). Los cambios entran en vigor en el próximo ciclo del agente.
      </div>
      <Field label="Máximo OTs por día (tope duro · A1)">
        <input
          type="number" min="0"
          value={zone.max_ots_dia ?? ""}
          onChange={(e) => onChange({ max_ots_dia: e.target.value === "" ? null : Number(e.target.value) })}
          className="w-32 px-2 py-1.5 border rounded text-[12px]" data-testid="input-max-ots"
          placeholder="Ej: 40"
        />
      </Field>
      <Field label="Prioridad Residencial">
        <label className="flex items-center gap-2 text-[12px]">
          <input
            type="checkbox" checked={!!zone.priority_residencial}
            onChange={(e) => onChange({ priority_residencial: e.target.checked })}
            data-testid="chk-priority-residencial"
          />
          <span>Priorizar OTs de plan Residencial al asignar</span>
        </label>
      </Field>
      <Field label="Tipos de plan BLOQUEADOS en esta zona">
        <div className="flex flex-wrap gap-1.5">
          {PLAN_TYPES.map((p) => {
            const isBlocked = (zone.blocked_plan_types || []).includes(p);
            return (
              <button
                key={p} type="button" onClick={() => toggleBlockedPlan(p)}
                className={`text-[10px] px-2 py-1 rounded border ${isBlocked ? "bg-red-100 border-red-300 text-red-700" : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"}`}
                data-testid={`chip-plan-${p}`}
              >
                {isBlocked && <X size={9} className="inline mr-0.5"/>}
                {p}
              </button>
            );
          })}
        </div>
        <div className="text-[9px] text-gray-500 mt-1">
          Click para bloquear/desbloquear. Los agentes A2/A3 no agendarán OTs de estos planes en esta zona.
        </div>
      </Field>
      <Field label="Multiplicador SLA (A3)">
        <input
          type="number" step="0.1" min="0.1" max="10"
          value={zone.sla_multiplier ?? 1.0}
          onChange={(e) => onChange({ sla_multiplier: Number(e.target.value) })}
          className="w-24 px-2 py-1.5 border rounded text-[12px]" data-testid="input-sla-mult"
        />
        <span className="text-[10px] text-gray-500 ml-2">1.0 = normal · 1.5 = zona lenta (+50% SLA) · 0.8 = zona rápida</span>
      </Field>
    </div>
  );
}

function TecnicosTab({ zone, technicians, onAdd, onRemove, onToggleAutoAssign }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQ, setPickerQ] = useState("");
  const [pickerSelected, setPickerSelected] = useState(new Set());

  const assignedIds = new Set((zone.technicians || []).map((t) => t.user_id));
  const available = technicians.filter((t) => {
    const id = String(t.id);
    if (assignedIds.has(id)) return false;
    if (!pickerQ.trim()) return true;
    const q = pickerQ.toLowerCase();
    return (t.name || "").toLowerCase().includes(q) || (t.email || "").toLowerCase().includes(q);
  });

  const confirmAdd = () => {
    const ids = Array.from(pickerSelected);
    if (ids.length) {
      onAdd(ids);
      setPickerSelected(new Set());
      setPickerOpen(false);
      setPickerQ("");
    }
  };

  return (
    <div className="space-y-3" data-testid="tecnicos-tab">
      <div className="flex items-center justify-between">
        <div className="text-[10px] text-gray-500">
          Técnicos asignados a esta zona. Los agentes A1/A2 los prefieren para OTs dentro del polígono.
          El check <b>Auto</b> desactivado saca al técnico del auto-agendamiento A2 (global: afecta todas las zonas) aunque tenga horario/zona.
        </div>
        <button
          onClick={() => setPickerOpen(true)}
          className="text-[11px] px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-1"
          data-testid="btn-add-tech"
        >
          <UserPlus size={12}/> Agregar técnicos
        </button>
      </div>

      {(zone.technicians || []).length === 0 ? (
        <div className="p-8 text-center text-[11px] text-gray-400 border border-dashed rounded" data-testid="no-techs">
          Sin técnicos asignados
        </div>
      ) : (
        <table className="w-full text-[11px]">
          <thead className="text-[9px] uppercase tracking-widest text-gray-500">
            <tr className="border-b">
              <th className="text-left p-2">Nombre</th>
              <th className="text-left p-2">Email</th>
              <th className="text-left p-2">Rol</th>
              <th className="text-left p-2">Asignado</th>
              <th className="text-center p-2" title="Si está desmarcado, A2 no le asigna OTs automáticamente">Auto</th>
              <th className="text-right p-2"></th>
            </tr>
          </thead>
          <tbody>
            {(zone.technicians || []).map((t) => {
              const autoOn = t.auto_assign_enabled !== false;
              return (
              <tr key={t.user_id} className="border-b hover:bg-gray-50" data-testid={`tech-row-${t.user_id}`}>
                <td className="p-2 font-semibold" style={{ color: "#0A3747" }}>{t.name}</td>
                <td className="p-2 text-gray-500">{t.email}</td>
                <td className="p-2 text-[10px]">{t.role}</td>
                <td className="p-2 text-[10px] text-gray-400 font-mono">{(t.assigned_at || "").slice(0, 10)}</td>
                <td className="p-2 text-center">
                  <label
                    className="inline-flex items-center gap-1 cursor-pointer"
                    title={autoOn
                      ? "Auto-agendamiento activo · clic para excluir de A2"
                      : "Excluido de A2 · clic para reactivar"}
                  >
                    <input
                      type="checkbox"
                      checked={autoOn}
                      onChange={(e) => onToggleAutoAssign?.(t.user_id, e.target.checked)}
                      className="w-4 h-4 accent-[#0A3747] cursor-pointer"
                      data-testid={`chk-auto-assign-${t.user_id}`}
                    />
                    <span className={`text-[9px] font-bold uppercase tracking-wider ${autoOn ? "text-emerald-700" : "text-amber-700"}`}>
                      {autoOn ? "Sí" : "No"}
                    </span>
                  </label>
                </td>
                <td className="p-2 text-right">
                  <button
                    onClick={() => onRemove(t.user_id)}
                    className="text-[10px] text-red-600 hover:bg-red-50 p-1 rounded"
                    data-testid={`btn-remove-tech-${t.user_id}`}
                    title="Quitar de zona"
                  >
                    <Trash2 size={12}/>
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {pickerOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setPickerOpen(false)}>
          <div className="bg-white rounded-lg w-[520px] max-h-[70vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="tech-picker">
            <div className="p-3 border-b flex items-center gap-2">
              <UserPlus size={16} className="text-blue-600"/>
              <div className="flex-1 font-semibold text-[13px]" style={{ color: "#0A3747" }}>Agregar técnicos a la zona</div>
              <button onClick={() => setPickerOpen(false)} className="p-1 hover:bg-gray-100 rounded"><X size={14}/></button>
            </div>
            <div className="p-2 border-b">
              <input
                type="text" autoFocus placeholder="Buscar por nombre o email…"
                value={pickerQ} onChange={(e) => setPickerQ(e.target.value)}
                className="w-full px-2 py-1.5 border rounded text-[12px]"
                data-testid="tech-picker-search"
              />
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {available.length === 0 ? (
                <div className="p-6 text-center text-[11px] text-gray-400">Sin coincidencias</div>
              ) : available.map((t) => {
                const id = String(t.id);
                const isSel = pickerSelected.has(id);
                return (
                  <label key={id} className="flex items-center gap-2 p-1.5 hover:bg-gray-50 rounded cursor-pointer" data-testid={`tech-picker-opt-${id}`}>
                    <input
                      type="checkbox" checked={isSel}
                      onChange={() => setPickerSelected((prev) => {
                        const nx = new Set(prev);
                        nx.has(id) ? nx.delete(id) : nx.add(id);
                        return nx;
                      })}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-semibold" style={{ color: "#0A3747" }}>{t.name}</div>
                      <div className="text-[10px] text-gray-500">{t.email} · {t.role}</div>
                    </div>
                  </label>
                );
              })}
            </div>
            <div className="p-3 border-t flex items-center justify-between bg-gray-50">
              <div className="text-[10px] text-gray-500">{pickerSelected.size} seleccionados</div>
              <div className="flex gap-2">
                <button onClick={() => setPickerOpen(false)} className="text-[11px] px-3 py-1.5 border rounded">Cancelar</button>
                <button
                  onClick={confirmAdd} disabled={!pickerSelected.size}
                  className={`text-[11px] px-3 py-1.5 rounded ${pickerSelected.size ? "bg-blue-600 text-white hover:bg-blue-700" : "bg-gray-200 text-gray-400 cursor-not-allowed"}`}
                  data-testid="btn-confirm-add-techs"
                >
                  Agregar {pickerSelected.size || ""}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold block mb-1">{label}</label>
      {children}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════
 * BodegasTab · v2.8.8.2 · Centralización geográfica
 * Muestra las bodegas de la sucursal a la que pertenece la zona, ya que
 * en el modelo actual las bodegas están asociadas al branch (padre de la
 * zona). Ofrece acceso rápido a "Bodegas · Mapeo Geográfico" para edición.
 * ═══════════════════════════════════════════════════════════════════════ */
function BodegasTab({ zone, branches }) {
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Encontrar el branch code de la zona
  const branch = useMemo(() => {
    if (!zone?.branch_id) return null;
    for (const b of branches || []) {
      if (b.branch_id === zone.branch_id) return b;
    }
    return null;
  }, [zone, branches]);

  useEffect(() => {
    if (!branch?.branch_code) { setWarehouses([]); return; }
    setLoading(true);
    api.get(`/inventory/warehouses?branch=${encodeURIComponent(branch.branch_code)}`)
      .then(({ data }) => {
        setWarehouses(data.items || []);
        setError(null);
      })
      .catch((e) => {
        setWarehouses([]);
        setError(e?.response?.data?.detail || "No se pudieron cargar las bodegas");
      })
      .finally(() => setLoading(false));
  }, [branch?.branch_code]);

  if (!zone?.branch_id) {
    return (
      <div className="p-6 text-center text-sm text-gray-500 bg-amber-50 border border-amber-200 rounded" data-testid="bodegas-sin-sucursal">
        <Warehouse size={32} className="mx-auto text-amber-500 mb-2" />
        <div className="font-semibold text-amber-800">Zona sin sucursal asignada</div>
        <p className="mt-1 text-[13px]">
          Asigna esta zona a una sucursal en la pestaña <b>Datos básicos</b> para ver
          las bodegas disponibles para la operación en este polígono.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="bodegas-tab">
      {/* Header con sucursal + shortcut */}
      <div className="flex items-center justify-between gap-3 pb-3 border-b border-gray-200">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-mono text-gray-500">
            Sucursal asociada
          </div>
          <div className="font-bold text-[#0A3747] text-sm truncate">
            {branch?.branch_name || "—"}
            {branch?.branch_code && (
              <span className="ml-2 text-[10px] font-mono text-gray-400">{branch.branch_code}</span>
            )}
          </div>
        </div>
        <a
          href="/inventario"
          target="_blank"
          rel="noreferrer"
          className="text-[11px] font-semibold flex items-center gap-1 px-2.5 py-1.5 rounded border border-[#0A3747] text-[#0A3747] hover:bg-[#0A3747] hover:text-white transition"
          data-testid="bodegas-goto-inventario"
        >
          <ExternalLink size={12} /> Abrir Inventario
        </a>
      </div>

      {/* Info operativa */}
      <div className="text-[12px] text-gray-600 bg-blue-50 border border-blue-200 rounded px-3 py-2 leading-relaxed">
        <b>💡 Cómo se relacionan bodegas y zonas:</b> en el modelo operativo, las
        bodegas se asocian a la <b>sucursal</b> que contiene la zona. Los técnicos
        asignados a esta zona (pestaña Técnicos) pueden retirar material de
        cualquier bodega listada abajo. Para gestionar el mapeo detallado
        técnico ↔ bodega, ve al módulo <b>Bodegas y Mapeo Geográfico</b>.
      </div>

      {/* Lista de bodegas */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="animate-spin text-gray-400" /></div>
      ) : error ? (
        <div className="p-3 rounded bg-red-50 border border-red-200 text-red-700 text-xs" data-testid="bodegas-error">
          {error}
        </div>
      ) : warehouses.length === 0 ? (
        <div className="text-center py-8 text-sm text-gray-400" data-testid="bodegas-empty">
          <Warehouse size={28} className="mx-auto mb-2 opacity-40" />
          No hay bodegas registradas para la sucursal <b>{branch?.branch_code}</b>.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="bodegas-list">
          {warehouses.map((w) => (
            <div
              key={w.warehouse_location_id || w.id}
              className="bg-white border border-gray-200 rounded-lg p-3 hover:border-[#0A3747]/30 transition"
              data-testid={`bodega-item-${w.warehouse_location_id || w.id}`}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${
                    w.kind === "central"
                      ? "bg-[#0A3747] text-[#E0E621]"
                      : "bg-gray-100 text-gray-500"
                  }`}
                >
                  <Warehouse size={14} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[9px] font-mono uppercase tracking-widest text-gray-400">
                    {w.kind === "central" ? "Bodega Central" : "Mini-bodega"}
                  </div>
                  <div className="font-semibold text-sm text-gray-900 truncate">
                    {w.location_name || w.name || "Sin nombre"}
                  </div>
                  {(w.technician_name || w.technician_id) && (
                    <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-1">
                      <Users size={10} /> {w.technician_name || w.technician_id}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="text-[10px] text-gray-400 text-center pt-2 border-t border-gray-100">
        {warehouses.length > 0 && (
          <>Total: <b>{warehouses.length}</b> bodega{warehouses.length !== 1 ? "s" : ""} en esta sucursal</>
        )}
      </div>
    </div>
  );
}
